import { Helmet } from 'react-helmet'
import React from 'react'
import { PUBLIC_LINK_BASE } from 'variables/general'
import { durationFormat } from 'variables/functions'
import { appEnvironment } from 'configs'

function makePackSchema(pack) {
  let result

  pack.photographer.review
    ? (result = {
        '@context': 'http://schema.org',
        '@type': 'Product',
        name: pack.title,
        image: pack.images[0].mobileThumbnail || pack.images[0].thumbnail,
        author: {
          '@type': 'Person',
          name: `${pack.photographer.name}`,
        },
        description: pack?.description,
        sku: `${pack.identifier}`,
        gtin: `${pack.identifier}`,
        brand: {
          '@type': 'Brand',
          name: `${pack.photographer.companyName}`,
        },
        review: {
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: '5',
            bestRating: '5',
          },
          author: {
            '@type': 'Person',
            name: 'Focal',
          },
        },
        price: pack.price,
        priceCurrency: `${
          pack?.photographer?.overrideCurrencyCode?.toUpperCase() ??
          pack.photographer.currencyCode.toUpperCase()
        }`,
        hours: pack.hoursOfCoverage,
        // photoNumbers: `${pack.numberOfPhotosMin} - ${pack.numberOfPhotosMax}`,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingCount: `${pack.photographer?.review?.number?.toString()}`,
          ratingValue: `${pack.photographer?.review?.rating}`,
        },
        offers: {
          '@type': 'Offer',
          url: `https://app.bookfocal.com/package/${pack.identifier}`,
          priceCurrency: `${
            pack?.photographer?.overrideCurrencyCode?.toUpperCase() ??
            pack.photographer.currencyCode.toUpperCase()
          }`,
          price: pack.price,
          priceValidUntil: '2030-11-20',
          availability: 'https://schema.org/InStock',
        },
      })
    : (result = {
        '@context': 'http://schema.org',
        '@type': 'Product',
        name: pack.title,
        image: pack.images[0].mobileThumbnail || pack.images[0].thumbnail,
        author: {
          '@type': 'Person',
          name: `${pack.photographer.name}`,
        },
        description: pack?.description,
        sku: `${pack.identifier}`,
        gtin: `${pack.identifier}`,
        brand: {
          '@type': 'Brand',
          name: `${pack.photographer.companyName}`,
        },
        review: {
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: '5',
            bestRating: '5',
          },
          author: {
            '@type': 'Person',
            name: 'Focal',
          },
        },
        price: pack.price,
        priceCurrency: `${
          pack?.photographer?.overrideCurrencyCode?.toUpperCase() ??
          pack.photographer.currencyCode.toUpperCase()
        }`,
        hours: pack.hoursOfCoverage,
        // photoNumbers: `${pack.numberOfPhotosMin} - ${pack.numberOfPhotosMax}`,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingCount: '5',
          ratingValue: '5',
        },
        offers: {
          '@type': 'Offer',
          url: `https://app.bookfocal.com/package/${pack.identifier}`,
          priceCurrency: `${
            pack?.photographer?.overrideCurrencyCode?.toUpperCase() ??
            pack.photographer.currencyCode.toUpperCase()
          }`,
          price: pack.price,
          priceValidUntil: '2030-11-20',
          availability: 'https://schema.org/InStock',
        },
      })

  return result
}

// The `PackageHelmet` component is a function component that takes `props` as its only argument.
export function PackageHelmet(props) {
  // It first destructures necessary properties from `props.pack.photographer` and `props.pack` itself.
  let city = props.pack.photographer.location.name
  let photographer = props.pack.photographer

  const currencySymbol =
    props?.pack?.photographer?.overrideCurrencyCode ?? props.pack.photographer.currencySymbol
  const currencyCode =
    (props?.pack?.photographer?.overrideCurrencyCode || props.pack.photographer.currencyCode) &&
    (props?.pack?.photographer?.overrideCurrencyCode?.toUpperCase() ??
      props.pack.photographer.currencyCode.toUpperCase())
  const price = Math.round(props.pack.price)
  const priceString = currencySymbol ? `${currencySymbol}${price}` : `${price} ${currencyCode}`

  // The title and description strings for the page are composed based on whether `city` is defined.
  // The description includes the package's description, with any HTML tags removed.
  const titleString = city
    ? `${priceString} - ${props.pack.title} - ${props.pack.photographer.companyName} - ${city}`
    : `${priceString} - ${props.pack.title} - ${props.pack.photographer.companyName}`

  const description = city
    ? `${priceString} - ${props.pack.title} - ${city} - ${durationFormat(
        props.pack.hoursOfCoverage,
      )} Session - ${props.pack?.description?.replace(/(<([^>]+)>)/gi, '')}`
    : `${priceString} - ${props.pack.title} - ${durationFormat(
        props.pack.hoursOfCoverage,
      )} Session - ${props.pack?.description?.replace(/(<([^>]+)>)/gi, '')}`

  // The return statement includes a `Helmet` component, which allows you to manage all of your head tags.

  let canonicalLink = props.pack.photographer.nextCustomDomain
    ? `https://${props.pack.photographer.nextCustomDomain}/package/${props.pack.identifier}`
    : props.pack.photographer.login
    ? `https://${props.pack.photographer.login}.website.bookfocal.com/package/${props.pack.identifier}`
    : `https://app.bookfocal.com/marketplace-package/${props.pack.identifier}`

  return (
    <Helmet>
      <title>{titleString}</title>
      <link rel="canonical" href={canonicalLink} />
      {/* The rest of the `Helmet` component consists of various `meta` tags and a `script` tag. These are also placed in the head of your HTML document, and they provide information about the web page to search engines and other services. */}
      <meta name="description" content={description} />
      <meta property="og:title" content={props.pack.title} />
      <meta property="og:description" content={props.pack?.description?.replace(/(<([^>]+)>)/gi, '')} />
      <meta property="og:url" content={`${PUBLIC_LINK_BASE(photographer)}${props.location.pathname}`} />
      <meta property="og:image" content={props.image} />
      <meta property="product:brand" content={props.pack.photographer.companyName} />
      <meta property="product:availability" content="in stock" />
      <meta property="product:condition" content="new" />
      <meta property="product:price:amount" content={props.pack.price} />
      <meta property="product:price:currency" content="CAD" />
      <meta
        property="product:retailer_item_id"
        content={props.pack.photographer.companyName + "'s " + props.pack.title}
      />
      <script key={`packJSON-${props.pack.identifier}`} type="application/ld+json">
        {`${JSON.stringify(makePackSchema(props.pack))}`}
      </script>
    </Helmet>
  )
}

export function MarketplacePackageHelmet(props) {
  let city = props.pack.photographer.location.name
  let photographer = props.pack.photographer

  const currencySymbol =
    props?.pack?.photographer?.overrideCurrencyCode ?? props.pack.photographer.currencySymbol
  const currencyCode =
    (props?.pack?.photographer?.overrideCurrencyCode || props.pack.photographer.currencyCode) &&
    (props?.pack?.photographer?.overrideCurrencyCode?.toUpperCase() ??
      props.pack.photographer.currencyCode.toUpperCase())

  const price = Math.round(props.pack.price)

  const priceString = currencySymbol ? `${currencySymbol}${price}` : `${price} ${currencyCode}`

  const titleString = city
    ? `${priceString} - ${props.pack.title} - ${props.pack.photographer.companyName} - ${city}`
    : `${priceString} - ${props.pack.title} - ${props.pack.photographer.companyName}`

  const description = city
    ? `${priceString} - ${props.pack.title} - ${city} - ${durationFormat(
        props.pack.hoursOfCoverage,
      )} Session - ${props.pack?.description?.replace(/(<([^>]+)>)/gi, '')}`
    : `${priceString} - ${props.pack.title} - ${durationFormat(
        props.pack.hoursOfCoverage,
      )} Session - ${props.pack?.description?.replace(/(<([^>]+)>)/gi, '')}`

  let canonicalLink =
    props.pack.photographer.nextCustomDomain && props.pack.photographer.activeWebsite
      ? `https://${props.pack.photographer.nextCustomDomain}/package/${
          props.pack.urlSlug || props.pack.identifier
        }`
      : props.pack.photographer.login && props.pack.photographer.activeWebsite
      ? `https://${props.pack.photographer.login}.website.bookfocal.com/package/${
          props.pack.urlSlug || props.pack.identifier
        }`
      : props.pack.photographer.domain
      ? `https://app.bookfocal.com/package/${props.pack.identifier}`
      : `https://app.bookfocal.com/marketplace-package/${props.pack.identifier}`

  return (
    <Helmet>
      <title>{titleString}</title>
      <link rel="canonical" href={canonicalLink} />
      <meta name="description" content={description} />
      <meta property="og:title" content={props.pack.title} />
      <meta property="og:description" content={props.pack?.description?.replace(/(<([^>]+)>)/gi, '')} />
      <meta property="og:url" content={`${PUBLIC_LINK_BASE(photographer)}${props.location.pathname}`} />
      <meta property="og:image" content={props.image} />
      <meta property="product:brand" content={props.pack.photographer.companyName} />
      <meta property="product:availability" content="in stock" />
      <meta property="product:condition" content="new" />
      <meta property="product:price:amount" content={props.pack.price} />
      <meta property="product:price:currency" content="CAD" />
      <meta
        property="product:retailer_item_id"
        content={props.pack.photographer.companyName + "'s " + props.pack.title}
      />
      <script key={`packJSON-${props.pack.identifier}`} type="application/ld+json">
        {`${JSON.stringify(makePackSchema(props.pack))}`}
      </script>
      <script>
        {appEnvironment === 'production'
          ? `
        fbq('track', 'ViewContent', {
        content_ids: "${props.pack.photographer.companyName}'s ${props.pack.title}",
        content_type: 'product',
        })`
          : null}
      </script>
    </Helmet>
  )
}
